<template>
  <div class="nds-markdown-editor">
    <Editor
      ref="markdownEditor"
      :height="`${height}px`"
      initial-edit-type="wysiwyg"
      :initial-value="currentValue"
      :options="editorOptions"
      :preview-style="previewStyle"
      @change="onEditorChange"
    />
  </div>
</template>

<script setup lang="ts">
import { Editor } from '@toast-ui/vue-editor'
import type { EditorOptions, PreviewStyle } from '@toast-ui/editor'
import Vue, { computed, onMounted, type PropType, ref, watch } from 'vue'
import '@toast-ui/editor/dist/i18n/fr-fr'

const getCurrentLang = Vue.prototype.getCurrentLang

const emit = defineEmits<(e: 'input', value: string) => void>()

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  height: {
    type: Number,
    default: 256,
  },
  previewStyle: {
    type: String as PropType<PreviewStyle>,
    default: 'tab',
  },
  showMode: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object as PropType<Partial<EditorOptions>>,
    default: () => ({}),
  },
})

const defaultEditorOptions: Partial<EditorOptions> = {
  language: getCurrentLang(),
  usageStatistics: false,
  hideModeSwitch: !props.showMode,
  toolbarItems: [
    ['heading', 'bold', 'italic'],
    ['hr', 'quote'],
    ['ul', 'image', 'link'],
  ],
  autofocus: false,
}

const editorOptions = computed(() => {
  return {
    ...defaultEditorOptions,
    ...props.options,
  }
})

const markdownEditor = ref()
const currentValue = ref(props.value ?? '')

const onEditorChange = () => {
  currentValue.value = markdownEditor.value.invoke('getMarkdown')
  emit('input', currentValue.value)
}

const onQuery = (query: string, payload: { popupName?: string } = {}) => {
  const editor = markdownEditor.value.editor

  let linkUrl = ''
  let linkText = ''

  // Check if query is from link popup
  if (query === 'getPopupInitialValues' && payload.popupName === 'link') {
    const range = editor.getSelection()
    const info = editor.getRangeInfoOfNode(Math.floor((range[0] + range[1]) / 2))

    if (info.type === 'link') {
      // init link popup props regarding caret position

      editor.setSelection(...info.range)
      let link = window.getSelection()?.getRangeAt(0).commonAncestorContainer.parentElement as
        | HTMLAnchorElement
        | null
        | undefined

      if (link) {
        link = link.closest('a') ?? link.querySelector('a') ?? link

        linkUrl = link.href
        linkText = link.innerText
      }
    } else {
      linkText = editor.getSelectedText()
    }
  }

  return {
    linkUrl,
    linkText,
  }
}

onMounted(() => {
  initMarkdownEditor()
})

const initMarkdownEditor = () => {
  if (!markdownEditor?.value?.editor) {
    return
  }

  // must remove default query event before registering a new one
  markdownEditor.value.editor.off('query')
  // event thrown when clicking on create link button
  markdownEditor.value.editor.on('query', onQuery)
}

watch(
  () => props.value,
  (newValue) => {
    if (newValue !== currentValue.value) {
      currentValue.value = newValue ?? ''
      // setMarkdown(markdown: string, cursorToEnd: boolean)
      markdownEditor.value.invoke('setMarkdown', currentValue.value, false)
    }
  }
)
</script>

<style lang="postcss" scoped>
.nds-markdown-editor {
  @apply tw-w-full;

  &::v-deep .toastui-editor-defaultUI {
    @apply tw-border-neutral-200;
  }
  &::v-deep .toastui-editor-defaultUI-toolbar {
    @apply tw-bg-neutral-50 tw-border-neutral-200;
  }

  &::v-deep .toastui-editor-contents {
    font-size: 14px;
    z-index: auto;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-style: normal;
      font-weight: 700;
      margin: 0;
      -webkit-font-smoothing: antialiased;
    }

    h1 {
      font-size: 40px;
      line-height: 52px;
    }
    h2 {
      font-size: 36px;
      line-height: 44px;
    }
    h3 {
      font-size: 32px;
      line-height: 40px;
    }
    h4 {
      font-size: 28px;
      line-height: 36px;
    }
    h5 {
      font-size: 24px;
      line-height: 32px;
    }
    h6 {
      font-size: 20px;
      line-height: 28px;
    }

    a {
      cursor: pointer;
    }
  }
}
</style>
